import React, {useCallback, useMemo, useRef, useState} from 'react';
import {createViewConfig, view} from '@sail/engine';
import {Popover} from '@sail/ui';
import {BasicIcon} from '@sail/helm';
import type {View} from '@sail/engine';
import {useIntl} from '@sail/intl';
import {Countries} from 'mkt-ssr/generated/globalization/countries';
import {styles} from 'mkt-ssr/components/sail/GlobalizationPicker/GlobalizationPickerStyles';
import {useGlobalizationPair} from 'mkt-ssr/lib/globalization/context';
import {GlobalizationPickerCountry} from 'mkt-ssr/components/sail/GlobalizationPicker/GlobalizationPickerCountry';
import {useRegionFormatter} from 'mkt-ssr/lib/globalization';
import {FocusOnMount} from 'mkt-ssr/components/sail/GlobalizationPicker/FocusOnMount';
import type {CountryCode, LocaleCode} from 'mkt-ssr/lib/globalization/types';
import {
  chooseCountryLanguage,
  availableLanguages,
  countryLanguageList,
} from 'mkt-ssr/components/sail/GlobalizationPicker/GlobalizationPicker.messages';

export type GlobalizationPickerProps = View.IntrinsicElement<'div'>;

export const GlobalizationPickerConfig = createViewConfig({
  props: {} as GlobalizationPickerProps,
  name: 'GlobalizationPicker',
});

type LanguageRefs = {
  [countryCode in CountryCode]?: {
    [localeCode in LocaleCode]?: HTMLAnchorElement | null;
  };
};
/**
 * The menu that allows users to choose a page translation
 */
export const GlobalizationPicker = GlobalizationPickerConfig.createView(
  (props) => {
    const {formatMessage} = useIntl();
    const languageRefs = useRef<LanguageRefs>({});

    const setLanguageRef = (
      el: HTMLAnchorElement | null,
      countryCode: CountryCode,
      localeCode: LocaleCode,
    ) => {
      if (!languageRefs.current[countryCode]) {
        languageRefs.current[countryCode] = {};
      }

      if (el) {
        languageRefs.current[countryCode]![localeCode] = el;
      } else if (languageRefs.current[countryCode]) {
        delete languageRefs.current[countryCode]![localeCode];
        if (Object.keys(languageRefs.current[countryCode]!).length === 0) {
          delete languageRefs.current[countryCode];
        }
      }
    };

    const formatRegionDisplayName = useRegionFormatter();
    const currentGlobalization = useGlobalizationPair();
    const globalizationPickerRef = useRef(null);
    const [globalizationPickerOpen, setGlobalizationPickerOpen] =
      useState(false);

    const activeCountry = currentGlobalization.country;
    const activeLocale = currentGlobalization.locale;
    const activeCountryDisplayName = formatRegionDisplayName(
      activeCountry.code,
    );
    const activeLanguageDisplayName = activeLocale.localizedName;
    const sortedCountries = useMemo(() => {
      return Object.values(Countries)
        .map((country) => {
          return {
            displayName: formatRegionDisplayName(country.code),
            countryData: country,
          };
        })
        .sort((a, b) => {
          return a.displayName.localeCompare(b.displayName);
        });
    }, [formatRegionDisplayName]);

    const handleFocusActiveLanguageLink = useCallback(() => {
      const refs = languageRefs.current;
      const countryCode = activeCountry.code;
      const localeCode = activeLocale.code;

      if (refs[countryCode]?.[localeCode]) {
        refs[countryCode]![localeCode]?.focus();
        return true;
      }

      return false;
    }, [activeCountry, activeLocale]);

    return (
      <>
        <view.button
          ref={globalizationPickerRef}
          onClick={() => setGlobalizationPickerOpen(!globalizationPickerOpen)}
          uses={[styles.buttonTrigger, styles.buttonTriggerUnprocessedCss]}
          aria-label={formatMessage(chooseCountryLanguage)}
          aria-expanded={globalizationPickerOpen}
          aria-haspopup="dialog"
        >
          <BasicIcon icon="compassArrow" />
          {activeCountryDisplayName} ({activeLanguageDisplayName})
        </view.button>

        <Popover
          placement="top left"
          targetRef={globalizationPickerRef}
          open={globalizationPickerOpen}
          onClose={() => setGlobalizationPickerOpen(false)}
          dismissible={{
            outsideFocus: false,
            escapeKey: true,
          }}
          flip={false}
          offset={10}
          shift={false}
          size={{padding: 0}}
          uses={[styles.popover, styles.popoverUnprocessed]}
          role="dialog"
          aria-label={formatMessage(availableLanguages)}
        >
          <view.div {...props}>
            <FocusOnMount onFocusMount={handleFocusActiveLanguageLink}>
              {/* @ts-expect-error Type fails in React 18 */}
              {({ref, onKeyDown}) => (
                <view.ul
                  uses={[styles.countryList, styles.countryListUnsupportedCss]}
                  ref={ref}
                  onKeyDown={onKeyDown}
                  aria-label={formatMessage(countryLanguageList)}
                >
                  {sortedCountries.map((country) => (
                    <GlobalizationPickerCountry
                      setLanguageRef={setLanguageRef}
                      key={country.countryData.code}
                      isActive={country.countryData === activeCountry}
                      country={country.countryData}
                      displayName={country.displayName}
                      testIdPrefix="footer-locale-"
                    />
                  ))}
                </view.ul>
              )}
            </FocusOnMount>
          </view.div>
        </Popover>
      </>
    );
  },
);
